// Recipe studio (backend plan C5): record a portal flow once from the
// production IP (the mail service drives the browser; the operator steers it
// element by element), review the draft as a form, run it in test mode, and
// publish it for the account. Secrets typed here stay in the browser session.
// API: /api/logistics/carrier-recipes/studio/sessions (open, act, undo,
// draft, screenshot, close), /drafts, /{id}/test-run, /{id}/status.

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, Check, CircleCheck, CircleHelp, CircleX, ExternalLink, Eye, Globe, Keyboard, ListChecks, ListOrdered,
  MousePointerClick, Play, Save, Search, Send, Undo2, Upload, WandSparkles, X,
} from 'lucide-react';
import { Badge, Banner, ConfirmDialog, Field, Tabs } from '../design/ui';
import './RecipeStudio.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const BASE = '/api/logistics/carrier-recipes';
const VARIABLE_CHOICES = ['tracking_number', 'motif', 'message', 'comments', 'package_content', 'package_value', 'recipient_phone', 'order_number', 'customer_email', 'username', 'password'];
const STATUS_TONE = { published: 'success', test: 'warning' };
const RUN_TONE = { success: 'success', stopped_before_submit: 'success', failed: 'danger', runner_unavailable: 'danger' };

const RecipeStudio = ({ carrierName, initialRecipe = null, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialRecipe ? 'review' : 'record');
  const [operation, setOperation] = useState(initialRecipe?.operation || 'create');
  const [startUrl, setStartUrl] = useState('');
  const [session, setSession] = useState(null);
  const [frame, setFrame] = useState(null);
  const [frameKey, setFrameKey] = useState(0);
  const [frameFailed, setFrameFailed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [value, setValue] = useState('');
  const [variable, setVariable] = useState('');
  const [secret, setSecret] = useState(false);
  const [waitText, setWaitText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(initialRecipe);
  const [testResult, setTestResult] = useState(null);
  const [saved, setSaved] = useState(initialRecipe?.db_id ? initialRecipe : null);
  const [filter, setFilter] = useState('');
  const [confirmRetire, setConfirmRetire] = useState(false);
  const sessionRef = useRef(null);

  const fail = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    setError(typeof detail === 'string' ? detail : fallback);
  };

  useEffect(() => () => {
    if (sessionRef.current) axios.delete(`${BASE}/studio/sessions/${sessionRef.current}`, authHeaders()).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape' && !confirmRetire) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, confirmRetire]);

  const showFrame = (nextFrame) => {
    setFrame(nextFrame);
    setFrameKey((k) => k + 1);
    setFrameFailed(false);
  };

  const openSession = async () => {
    setBusy(true); setError(null);
    try {
      const response = await axios.post(`${BASE}/studio/sessions`, { carrier: carrierName, operation, start_url: startUrl || null }, authHeaders());
      setSession(response.data.session);
      sessionRef.current = response.data.session.id;
      showFrame(response.data.frame);
    } catch (e) { fail(e, t('recipeStudio.openError', 'Impossible d’ouvrir la session d’enregistrement.')); }
    finally { setBusy(false); }
  };

  const act = async (action, extra = {}) => {
    if (!session) return;
    setBusy(true); setError(null);
    try {
      const body = { action, k: selected?.k ?? null, value: value || null, variable: variable || null, secret, wait_text: waitText || null, ...extra };
      const response = await axios.post(`${BASE}/studio/sessions/${session.id}/act`, body, authHeaders());
      showFrame(response.data.frame);
      setSelected(null); setValue(''); setVariable(''); setSecret(false); setWaitText('');
    } catch (e) { fail(e, t('recipeStudio.actError', 'L’action a échoué.')); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const response = await axios.post(`${BASE}/studio/sessions/${session.id}/undo`, {}, authHeaders());
      showFrame(response.data.frame);
    } catch (e) { fail(e, t('recipeStudio.actError', 'L’action a échoué.')); }
    finally { setBusy(false); }
  };

  const makeDraft = async (review) => {
    if (!session) return;
    setBusy(true); setError(null);
    try {
      const response = await axios.post(`${BASE}/studio/sessions/${session.id}/draft?review=${review ? 'true' : 'false'}`, {}, authHeaders());
      setDraft(response.data.draft);
      setMode('review');
    } catch (e) { fail(e, t('recipeStudio.draftError', 'Impossible de produire le brouillon.')); }
    finally { setBusy(false); }
  };

  const updateStep = (index, patch) => setDraft((d) => ({ ...d, steps: d.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));
  const removeStep = (index) => setDraft((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== index) }));
  const moveStep = (index, dir) => setDraft((d) => {
    const steps = [...d.steps]; const j = index + dir;
    if (j < 0 || j >= steps.length) return d;
    [steps[index], steps[j]] = [steps[j], steps[index]];
    return { ...d, steps };
  });

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const response = await axios.post(`${BASE}/drafts`, { recipe: draft }, authHeaders());
      setSaved(response.data.recipe);
      setDraft(response.data.recipe);
      if (onSaved) onSaved(response.data.recipe);
    } catch (e) { fail(e, t('recipeStudio.saveError', 'Le brouillon n’a pas pu être enregistré.')); }
    finally { setBusy(false); }
  };

  const testRun = async () => {
    if (!saved) return;
    setBusy(true); setError(null); setTestResult(null);
    try {
      const variables = {};
      Object.entries(draft.sample_values || {}).forEach(([k, v]) => { if (v && k !== 'password') variables[k] = v; });
      const response = await axios.post(`${BASE}/${encodeURIComponent(saved.db_id || saved.id)}/test-run`, { variables }, authHeaders());
      setTestResult(response.data);
      if (response.data.recipe) { setSaved(response.data.recipe); setDraft((d) => ({ ...d, last_test: response.data.recipe.last_test, status: response.data.recipe.status })); }
    } catch (e) { fail(e, t('recipeStudio.testError', 'La run de test a échoué.')); }
    finally { setBusy(false); }
  };

  const setStatus = async (status, catalog_proposal) => {
    if (!saved) return;
    setBusy(true); setError(null);
    try {
      const response = await axios.post(`${BASE}/${encodeURIComponent(saved.db_id || saved.id)}/status`, { status, catalog_proposal }, authHeaders());
      setSaved(response.data.recipe); setDraft(response.data.recipe);
      if (onSaved) onSaved(response.data.recipe);
    } catch (e) { fail(e, t('recipeStudio.statusError', 'Le changement de statut a été refusé.')); }
    finally { setBusy(false); }
  };

  const elements = (frame?.elements || []).filter((e) => !filter || JSON.stringify(e).toLowerCase().includes(filter.toLowerCase()));
  const canPublish = saved && draft && (draft.validation_errors || []).length === 0 && ['stopped_before_submit', 'success'].includes(draft.last_test?.status);
  const published = draft?.status === 'published';
  const elementName = (e) => e.label || e.text || e.placeholder || e.id || e.name || e.css;

  const renderStart = () => (
    <div className="rst-v2__start">
      <Banner tone="info">
        {t('recipeStudio.startIntro', 'Le navigateur tourne sur le serveur (IP de production). Réalisez le parcours une fois : choisissez un élément, une action, et marquez les valeurs qui varient d’un ticket à l’autre. Rien n’est envoyé au transporteur : l’étape d’envoi est seulement enregistrée.')}
      </Banner>
      <div className="u-form-grid">
        <Field label={t('recipeStudio.operation', 'Opération')} htmlFor="rst-operation">
          <select id="rst-operation" className="u-select" value={operation} onChange={(e) => setOperation(e.target.value)}>
            <option value="create">{t('carrierRecipes.op.create', 'création')}</option>
            <option value="update">{t('carrierRecipes.op.update', 'relance')}</option>
            <option value="monitor">{t('carrierRecipes.op.monitor', 'surveillance')}</option>
            <option value="pod">{t('carrierRecipes.op.pod', 'preuve')}</option>
          </select>
        </Field>
        <Field label={t('recipeStudio.startUrl', 'URL du portail (optionnel si connue)')} htmlFor="rst-start-url">
          <input id="rst-start-url" className="u-input" value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://…" />
        </Field>
      </div>
      <div>
        <button type="button" className="u-btn u-btn--primary" onClick={openSession} disabled={busy}>
          <Globe aria-hidden="true" />
          {busy ? t('recipeStudio.opening', 'Ouverture…') : t('recipeStudio.open', 'Ouvrir le portail')}
        </button>
      </div>
    </div>
  );

  const renderRecord = () => (
    <div className="rst-v2__record">
      <div className="rst-v2__frame">
        <div className="rst-v2__url" title={frame?.url}>{frame?.url}</div>
        <div className="rst-v2__screen">
          {frameFailed ? (
            <span className="u-muted">{t('carriersUi.studio.noScreenshot', 'Capture indisponible : l’action suivante la rafraîchit.')}</span>
          ) : (
            <img
              key={frameKey}
              src={`${BASE}/studio/sessions/${session.id}/screenshot?t=${frameKey}`}
              alt={t('recipeStudio.frameAlt', 'Page du portail')}
              onError={() => setFrameFailed(true)}
            />
          )}
        </div>
        <div className="rst-v2__frame-bar">
          <span className="rst-v2__count">
            {t('carriersUi.studio.recorded', 'Étapes enregistrées : {{n}}', { n: frame?.steps ?? 0 })}
          </span>
          <span className="rst-v2__spacer" />
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={undo} disabled={busy}>
            <Undo2 aria-hidden="true" />
            {t('recipeStudio.undo', 'Annuler la dernière')}
          </button>
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => makeDraft(true)} disabled={busy}>
            <WandSparkles aria-hidden="true" />
            {t('recipeStudio.finishReview', 'Terminer avec relecture IA')}
          </button>
          <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => makeDraft(false)} disabled={busy}>
            <ListChecks aria-hidden="true" />
            {t('carriersUi.studio.finish', 'Terminer et relire')}
          </button>
        </div>
      </div>
      <div className="rst-v2__side">
        <div className="u-input-group u-input-group--lead">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            className="u-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('recipeStudio.filter', 'Filtrer les éléments…')}
            aria-label={t('recipeStudio.filter', 'Filtrer les éléments…')}
          />
        </div>
        <div className="rst-v2__elements" role="listbox" aria-label={t('carriersUi.studio.elements', 'Éléments de la page')}>
          {elements.map((e) => (
            <button
              type="button"
              key={e.k}
              role="option"
              aria-selected={selected?.k === e.k}
              className={`rst-v2__el${selected?.k === e.k ? ' is-selected' : ''}`}
              onClick={() => setSelected(e)}
              title={e.css || ''}
            >
              <span className="rst-v2__el-role">{e.role || e.tag}</span>
              <span className="rst-v2__el-name">{elementName(e)}</span>
              {e.options && <span className="rst-v2__el-opts">{e.options.length} {t('recipeStudio.options', 'options')}</span>}
            </button>
          ))}
          {elements.length === 0 && <div className="u-muted rst-v2__el-empty">{t('recipeStudio.noElements', 'Aucun élément interactif visible.')}</div>}
        </div>
        <div className="rst-v2__actions">
          <div className="rst-v2__selected">
            {selected
              ? <>{t('recipeStudio.selected', 'Élément')} : <strong>{elementName(selected)}</strong></>
              : <span className="u-muted">{t('carriersUi.studio.pick', 'Choisissez un élément dans la liste.')}</span>}
          </div>
          <div className="rst-v2__row">
            <input
              className="u-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('recipeStudio.valuePlaceholder', 'Valeur à saisir / option à choisir')}
              aria-label={t('recipeStudio.valuePlaceholder', 'Valeur à saisir / option à choisir')}
              type={secret ? 'password' : 'text'}
            />
            <select className="u-select" value={variable} onChange={(e) => setVariable(e.target.value)} aria-label={t('carriersUi.studio.variable', 'Variable')}>
              <option value="">{t('recipeStudio.literal', 'Valeur fixe')}</option>
              {VARIABLE_CHOICES.map((v) => <option key={v} value={v}>{`{${v}}`}</option>)}
            </select>
          </div>
          <label className="rst-v2__check">
            <input type="checkbox" checked={secret} onChange={(e) => setSecret(e.target.checked)} />
            {t('carriersUi.studio.secret', 'Valeur secrète (masquée dans la recette)')}
          </label>
          <div className="rst-v2__buttons">
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('click')} disabled={busy || !selected}><MousePointerClick aria-hidden="true" />{t('recipeStudio.click', 'Cliquer')}</button>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('fill')} disabled={busy || !selected}><Keyboard aria-hidden="true" />{t('recipeStudio.fill', 'Saisir')}</button>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('select')} disabled={busy || !selected}><ListOrdered aria-hidden="true" />{t('recipeStudio.select', 'Choisir')}</button>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('upload')} disabled={busy || !selected}><Upload aria-hidden="true" />{t('recipeStudio.upload', 'Pièces jointes')}</button>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('submit')} disabled={busy || !selected}><Send aria-hidden="true" />{t('recipeStudio.submit', 'Marquer comme envoi')}</button>
          </div>
          <div className="rst-v2__row">
            <input
              className="u-input"
              value={waitText}
              onChange={(e) => setWaitText(e.target.value)}
              placeholder={t('recipeStudio.waitPlaceholder', 'Texte attendu sur la page')}
              aria-label={t('recipeStudio.waitPlaceholder', 'Texte attendu sur la page')}
            />
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('wait_text')} disabled={busy || !waitText}>{t('recipeStudio.wait', 'Attendre')}</button>
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('assert_text')} disabled={busy || !waitText}>{t('recipeStudio.assert', 'Vérifier')}</button>
          </div>
          <div className="rst-v2__row">
            <input className="u-input" value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://…" aria-label={t('carriersUi.studio.url', 'Adresse de la page')} />
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => act('navigate', { url: startUrl })} disabled={busy || !startUrl}>
              <Globe aria-hidden="true" />
              {t('recipeStudio.navigate', 'Aller à')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTest = () => {
    const run = testResult?.run;
    if (!run) return null;
    return (
      <div className={`rst-v2__test rst-v2__test--${RUN_TONE[run.status] || 'neutral'}`}>
        <div className="rst-v2__test-head">
          <Badge tone={RUN_TONE[run.status]} dot>{t(`carrierRecipes.runStatus.${run.status}`, run.status)}</Badge>
          {run.reached_step && <span className="u-muted">{run.reached_step}</span>}
        </div>
        {run.error && <Banner tone="danger">{run.error}</Banner>}
        <div className="rst-v2__test-steps">
          {(run.steps || []).map((s) => (
            <span key={s.id} title={[s.strategy, s.selector, s.error].filter(Boolean).join(' · ')}>
              <Badge tone={s.status === 'ok' ? 'success' : s.status === 'failed' ? 'danger' : undefined} icon={s.status === 'ok' ? CircleCheck : s.status === 'failed' ? CircleX : CircleHelp}>
                {s.id}
              </Badge>
            </span>
          ))}
        </div>
        {(testResult.captures || []).length > 0 && (
          <div className="rst-v2__captures">
            {testResult.captures.map((c, i) => (c.url ? (
              // eslint-disable-next-line react/no-array-index-key
              <a key={i} className="u-link" href={c.url} target="_blank" rel="noopener noreferrer">
                {c.step || t('carriersUi.recipes.capture', 'capture {{n}}', { n: i + 1 })}
                <ExternalLink aria-hidden="true" />
              </a>
            ) : null))}
          </div>
        )}
      </div>
    );
  };

  const renderReview = () => (
    <div className="rst-v2__review">
      <div className="rst-v2__review-head">
        <div className="rst-v2__review-id">
          <code className="u-code">{draft.id}</code>
          <span className="u-muted">v{draft.version}</span>
          <Badge tone={STATUS_TONE[draft.status]} dot>{t(`carrierRecipes.state.${draft.status}`, draft.status)}</Badge>
          {draft.last_test && (
            <span className="u-muted">
              {t('recipeStudio.lastTest', 'dernier test')} : {t(`carrierRecipes.runStatus.${draft.last_test.status}`, draft.last_test.status)}
            </span>
          )}
        </div>
        <div className="rst-v2__review-actions">
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={save} disabled={busy || published}>
            <Save aria-hidden="true" />
            {t('recipeStudio.save', 'Enregistrer le brouillon')}
          </button>
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={testRun} disabled={busy || !saved}>
            <Play aria-hidden="true" />
            {t('recipeStudio.test', 'Run de test')}
          </button>
          {published && (
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setStatus('published', true)} disabled={busy}>
              {t('recipeStudio.propose', 'Proposer au catalogue')}
            </button>
          )}
          {published && (
            <button type="button" className="u-btn u-btn--danger u-btn--sm" onClick={() => setConfirmRetire(true)} disabled={busy}>
              {t('recipeStudio.retire', 'Retirer')}
            </button>
          )}
          <button
            type="button"
            className="u-btn u-btn--primary u-btn--sm"
            onClick={() => setStatus('published')}
            disabled={busy || !canPublish}
            title={canPublish ? undefined : t('carriersUi.studio.publishHint', 'Enregistrez le brouillon et réussissez une run de test pour publier.')}
          >
            <Check aria-hidden="true" />
            {t('recipeStudio.publish', 'Publier pour ce compte')}
          </button>
        </div>
      </div>
      {(draft.validation_errors || []).length > 0 && <Banner tone="danger">{draft.validation_errors.join(' · ')}</Banner>}
      {(draft.review_notes || []).length > 0 && (
        <Banner tone="warning" title={t('carriersUi.studio.reviewNotes', 'Relecture de l’IA')}>
          <ul className="rst-v2__notes">
            {/* eslint-disable-next-line react/no-array-index-key */}
            {draft.review_notes.map((note, i) => <li key={i}>{note}</li>)}
          </ul>
        </Banner>
      )}
      <div className="u-form-grid">
        <Field label={t('recipeStudio.description', 'Description')} htmlFor="rst-description">
          <input id="rst-description" className="u-input" value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} disabled={published} />
        </Field>
        <div className="u-field">
          <span className="u-label">{t('recipeStudio.variables', 'Variables')}</span>
          <div className="rst-v2__vars">
            {(draft.variables || []).length
              ? draft.variables.map((v) => <code key={v} className="u-code">{`{${v}}`}</code>)
              : <span className="u-muted">—</span>}
          </div>
        </div>
      </div>
      <ol className="rst-v2__steps">
        {draft.steps.map((s, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={s.id + i} className={`rst-v2__step${s.type === 'submit' ? ' rst-v2__step--submit' : ''}`}>
            <span className="rst-v2__step-n u-num">{i + 1}</span>
            <input className="u-input rst-v2__step-id" value={s.id} onChange={(e) => updateStep(i, { id: e.target.value })} disabled={published} aria-label={t('carriersUi.studio.stepId', 'Nom de l’étape {{n}}', { n: i + 1 })} />
            <Badge tone={s.type === 'submit' ? 'danger' : undefined}>{s.type}</Badge>
            {['fill', 'select'].includes(s.type) && (
              <input className="u-input rst-v2__step-value" value={s.value ?? ''} onChange={(e) => updateStep(i, { value: e.target.value })} disabled={published} aria-label={t('carriersUi.studio.stepValue', 'Valeur de l’étape {{n}}', { n: i + 1 })} />
            )}
            {s.type === 'open' && <span className="rst-v2__step-target">{s.url}</span>}
            {['wait', 'assert'].includes(s.type) && <span className="rst-v2__step-target">{s.text || (s.any || []).map((c) => c.text).filter(Boolean).join(' | ')}</span>}
            {s.target && (
              <span className="rst-v2__step-target" title={JSON.stringify(s.target)}>
                {(s.target.any || [s.target]).map((tt) => Object.entries(tt).map(([k, v]) => `${k}=${v}`).join(' ')).join(' | ')}
              </span>
            )}
            {s.type !== 'open' && (
              <label className="rst-v2__check">
                <input type="checkbox" checked={!!s.optional} onChange={(e) => updateStep(i, { optional: e.target.checked })} disabled={published} />
                {t('recipeStudio.optional', 'optionnel')}
              </label>
            )}
            {!published && (
              <span className="rst-v2__step-tools">
                <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => moveStep(i, -1)} aria-label={t('carriersUi.studio.up', 'Monter')} title={t('carriersUi.studio.up', 'Monter')}><ArrowUp aria-hidden="true" /></button>
                <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => moveStep(i, 1)} aria-label={t('carriersUi.studio.down', 'Descendre')} title={t('carriersUi.studio.down', 'Descendre')}><ArrowDown aria-hidden="true" /></button>
                <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => removeStep(i)} aria-label={t('carriersUi.studio.remove', 'Retirer l’étape')} title={t('carriersUi.studio.remove', 'Retirer l’étape')}><X aria-hidden="true" /></button>
              </span>
            )}
          </li>
        ))}
      </ol>
      {draft.enums && Object.keys(draft.enums).length > 0 && (
        <div className="rst-v2__enums">
          <div className="u-overline">{t('recipeStudio.enums', 'Listes relevées sur le portail')}</div>
          <dl className="u-dl">
            {Object.entries(draft.enums).map(([field, options]) => (
              <div key={field}>
                <dt>{field}</dt>
                <dd>{options.join(' · ')}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {renderTest()}
    </div>
  );

  return (
    <div className="rst-v2__overlay" role="dialog" aria-modal="true" aria-label={t('recipeStudio.title', 'Studio de recettes')}>
      <div className="rst-v2__panel">
        <div className="rst-v2__head">
          <div className="rst-v2__title">
            <span className="u-overline">{t('recipeStudio.title', 'Studio de recettes')}</span>
            <h2>{carrierName}</h2>
          </div>
          <Tabs
            label={t('recipeStudio.title', 'Studio de recettes')}
            active={mode}
            onChange={(next) => { if (next === 'record' || draft) setMode(next); }}
            tabs={[
              { key: 'record', label: t('recipeStudio.tabRecord', 'Enregistrer'), icon: Eye },
              { key: 'review', label: t('recipeStudio.tabReview', 'Relire et publier'), icon: ListChecks },
            ]}
          />
          <button type="button" className="u-btn u-btn--ghost u-btn--icon" onClick={onClose} title={t('recipeStudio.close', 'Fermer')} aria-label={t('recipeStudio.close', 'Fermer')}>
            <X aria-hidden="true" />
          </button>
        </div>
        {error && <div className="rst-v2__error"><Banner tone="danger">{error}</Banner></div>}
        <div className="rst-v2__body">
          {mode === 'record' && !session && renderStart()}
          {mode === 'record' && session && renderRecord()}
          {mode === 'review' && draft && renderReview()}
          {mode === 'review' && !draft && <p className="u-muted">{t('carriersUi.studio.noDraft', 'Terminez un enregistrement pour relire son brouillon.')}</p>}
        </div>
      </div>
      {confirmRetire && (
        <ConfirmDialog
          title={t('carriersUi.studio.retireTitle', 'Retirer la recette {{id}} ?', { id: draft?.id })}
          message={t('carriersUi.studio.retireMessage', 'Elle ne sera plus utilisée pour les demandes de ce compte auprès de {{carrier}}.', { carrier: carrierName })}
          confirmLabel={t('recipeStudio.retire', 'Retirer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          onConfirm={async () => { setConfirmRetire(false); await setStatus('retired'); }}
          onCancel={() => setConfirmRetire(false)}
          busy={busy}
        />
      )}
    </div>
  );
};

export default RecipeStudio;
